import { useState, useEffect } from "react";
import { Controller, useWatch, Control } from "react-hook-form";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import { Vocabulary } from "../Utils/Vocabulary";
import { PurchaseTypes } from "../Utils/autocompleteEnum";
import { europeanDate } from "../Utils/formatDate";
import style from "../Styles/vehicleInfo.module.css";

interface Props {
  control: Control<any>;
  disabled: boolean;
  setValue: (name: string, value: any) => void;
}

/**
 * Purchase types that require ITP and RCA validity fields to be displayed in the form.
 * These types are: BuyBack, National, and Intermediated.
 */
const ITP_RCA_TYPES = new Set([
  PurchaseTypes.buyBack,
  PurchaseTypes.national,
  PurchaseTypes.intemediated,
]);

/**
 * VehicleRarSection component renders the RAR and ITP/RCA related fields based on the purchase type of the vehicle.
 * It conditionally displays the RAR completed and scheduled checkboxes, as well as the scheduled date picker for import vehicles.
 * For BuyBack, National, and Intermediated purchase types, it displays the ITP and RCA validity date pickers.
 * @param param0
 * @returns
 */
export default function VehicleRarSection({
  control,
  disabled,
  setValue,
}: Props) {
  const tipAchizitie = useWatch({ control, name: "tip_achizitie_r" });
  const rarScheduledDate = useWatch({ control, name: "rar_scheduled_date" });

  const [rarScheduled, setRarScheduled] = useState(() => !!rarScheduledDate);

  /**
   * Sets the RAR scheduled state based on the scheduled date.
   */
  useEffect(() => {
    setValue("rar_scheduled", !!rarScheduledDate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handles the toggle of the RAR scheduled checkbox.
   * If the checkbox is unchecked, it also sets the scheduled date to null.
   * @param checked
   */
  const handleRarScheduledToggle = (checked: boolean) => {
    setRarScheduled(checked);
    setValue("rar_scheduled", checked);
    if (!checked) setValue("rar_scheduled_date", null);
  };

  const purchaseTypeName = tipAchizitie?.nume;
  const isImport = purchaseTypeName === PurchaseTypes.import;
  const showItpRca = ITP_RCA_TYPES.has(purchaseTypeName);

  if (!isImport && !showItpRca) return null;

  /**
   *
   */
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      {/* Import: RAR Efectuat + Programat RAR + Data programării */}
      {isImport && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
            <Controller
              name="rar_completed"
              control={control}
              render={({ field: { value, onChange, ref } }) => (
                <FormControlLabel
                  label={
                    <Typography variant="body2">
                      {Vocabulary.rarEfectuat}
                    </Typography>
                  }
                  control={
                    <Checkbox
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                      inputRef={ref}
                      disabled={disabled}
                      size="small"
                    />
                  }
                />
              )}
            />
            <FormControlLabel
              label={
                <Typography variant="body2">
                  {Vocabulary.rarProgramat}
                </Typography>
              }
              control={
                <Checkbox
                  checked={rarScheduled}
                  onChange={(e) => handleRarScheduledToggle(e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
            />
          </Box>

          {rarScheduled && (
            <Controller
              name="rar_scheduled_date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  {...field}
                  format={europeanDate}
                  views={["month", "year", "day"]}
                  className={style.textField}
                  label={`${Vocabulary.dataProgramareRar} *`}
                  disabled={disabled}
                  value={field.value ? moment(field.value) : null}
                  onChange={(val) => field.onChange(val)}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          )}
        </Box>
      )}

      {/* BuyBack / Național / Intermediere: ITP & RCA validity */}
      {showItpRca && (
        <Box sx={{ mt: 1 }}>
          <Controller
            name="itp_valid_until"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                {...field}
                format={europeanDate}
                views={["month", "year", "day"]}
                className={style.textField}
                label={Vocabulary.itpValidUntil}
                disabled={disabled}
                value={field.value ? moment(field.value) : null}
                onChange={(val) => field.onChange(val)}
                slotProps={{
                  textField: {
                    size: "small",
                    error: !!error,
                    helperText: error?.message,
                  },
                }}
              />
            )}
          />
          <Controller
            name="rca_valid_until"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                {...field}
                format={europeanDate}
                views={["month", "year", "day"]}
                className={style.textField}
                label={Vocabulary.rcaValidUntil}
                disabled={disabled}
                value={field.value ? moment(field.value) : null}
                onChange={(val) => field.onChange(val)}
                slotProps={{
                  textField: {
                    size: "small",
                    error: !!error,
                    helperText: error?.message,
                  },
                }}
              />
            )}
          />
        </Box>
      )}
    </LocalizationProvider>
  );
}
